"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  nextIllustrationIndex,
  WAITING_ILLUSTRATIONS,
} from "@/data/waitingIllustrations";
import {
  shuffledTipOrder,
  WAITING_TIP_KIND_LABELS,
  WAITING_TIPS,
} from "@/data/waitingTips";

/** 読み物を自動で切り替える既定の間隔。 */
const DEFAULT_INTERVAL_MS = 7_000;

/**
 * 描画を始めるまでの待ち時間。
 * 認証や取得がすぐ終わったときにカードが一瞬光って消えるのを防ぐ。
 * DESIGN.md「短時間（< 300ms 想定）は何も表示しない（ちらつき防止）」に合わせる。
 */
const APPEAR_DELAY_MS = 400;

/** globals.css の `.waiting-tip-fade` の transition と揃える。 */
const FADE_MS = 300;

const KIND_LABEL_CLASS: Record<keyof typeof WAITING_TIP_KIND_LABELS, string> = {
  tip: "text-primary",
  quote: "text-accent",
};

interface WaitingTipCardProps {
  /**
   * 処理中であることを伝える文言（「考え中です...」など）。
   * カードの中に入れて、待機の案内と読み物を1つのまとまりとして見せる。
   */
  statusMessage?: string;
  /** 読み物を切り替える間隔（ミリ秒）。 */
  intervalMs?: number;
  /** 外枠に足すクラス。挿入先ごとの余白や最大幅を渡す。 */
  className?: string;
}

/**
 * フィードバック生成の待ち時間に、めんれん先輩のイラストと読み物を出すカード。
 *
 * 待機が終わって呼び出し側がこの部品を描画しなくなれば、切り替えも止まる。
 * イラストは全枚数を重ねて描画し、表示切替は visibility で行う（切り替え時に読み込みが
 * 走って絵が一瞬消えるのを防ぐ）。フェードは globals.css のクラスに寄せており、
 * モーション低減設定への追従もそちら側で行う。
 */
export default function WaitingTipCard({
  statusMessage,
  intervalMs = DEFAULT_INTERVAL_MS,
  className = "",
}: WaitingTipCardProps) {
  const [visible, setVisible] = useState(false);
  // 読み物を出す順番。表示開始時にシャッフルして、以降はこの順に送る
  const [tipOrder, setTipOrder] = useState<readonly number[]>(() =>
    WAITING_TIPS.map((_, index) => index),
  );
  const [tipPosition, setTipPosition] = useState(0);
  const [illustrationIndex, setIllustrationIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  // 手送りしたときに自動切り替えの間隔を計り直すためのキー
  const [restartKey, setRestartKey] = useState(0);

  const fadeTimerRef = useRef<number | null>(null);

  // 読み物の順番と最初のイラストは描画を始める直前に決める。
  // 描画前に確定するのでサーバー描画とクライアント描画の食い違いは起きず、
  // 内容が入れ替わる様子も画面には出ない。
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setTipOrder(shuffledTipOrder());
      setTipPosition(0);
      setIllustrationIndex(
        Math.floor(Math.random() * WAITING_ILLUSTRATIONS.length),
      );
      setVisible(true);
    }, APPEAR_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, []);

  const advance = useCallback(() => {
    // フェード中の呼び出しは無視する。タイマーを引き直すと、連打の間ずっと
    // 不透明度0のまま（空のカード）に見えてしまう。
    if (fadeTimerRef.current !== null) return;

    setIsFading(true);

    // 乱数は更新関数の外で引く。更新関数の中で引くと同じ入力から別の結果が出る
    // 非純粋な更新関数になり、StrictMode の二重呼び出しで結果が変わる。
    const random = Math.random();

    fadeTimerRef.current = window.setTimeout(() => {
      // シャッフル済みの順番を送る。一巡したら先頭へ戻る
      setTipPosition((position) => (position + 1) % WAITING_TIPS.length);
      setIllustrationIndex((index) => nextIllustrationIndex(index, random));
      setIsFading(false);
      fadeTimerRef.current = null;
    }, FADE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (fadeTimerRef.current !== null) {
        window.clearTimeout(fadeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!visible) return;
    const interval = window.setInterval(advance, intervalMs);
    return () => window.clearInterval(interval);
  }, [visible, intervalMs, advance, restartKey]);

  const handleAdvance = () => {
    advance();
    setRestartKey((key) => key + 1);
  };

  if (!visible) return null;

  const tip = WAITING_TIPS[tipOrder[tipPosition]];

  // 淡ピンクの背景色はアプリ全体の背景色と近いため、枠線がないと境界が見えなくなる。
  return (
    <button
      type="button"
      onClick={handleAdvance}
      data-testid="waiting-tip-card"
      className={`block w-full rounded-xl border border-primary/15 bg-pink-100 p-4 text-left transition-opacity hover:opacity-90 md:p-5 ${className}`}
    >
      <span className="sr-only">次の読み物を表示する</span>
      {/*
        処理中の案内はフェードさせる要素の外に置く。読み物と一緒に薄くなると、
        処理が続いているのかどうか分からなくなる。
      */}
      {statusMessage && (
        <span className="mb-3 block text-center text-sm font-medium text-subtext">
          {statusMessage}
        </span>
      )}
      {/*
        狭い画面では縦積みにする。横並びのままだと本文の列が狭くなり、
        文言の長さで行数が変わってカードの高さが7秒ごとに動いてしまう。
      */}
      <span
        className="waiting-tip-fade flex flex-col items-center gap-3 text-center sm:flex-row sm:gap-4 sm:text-left"
        data-fading={isFading}
      >
        {/*
          イラストは余白を切り詰めたうえで正方形に正規化してあるので、枠も正方形にする。
          全ての絵の縦横比が揃っているため、どの絵でも同じ大きさで表示される。
        */}
        <span className="relative size-24 shrink-0 md:size-28">
          {WAITING_ILLUSTRATIONS.map((illustration, index) => {
            const isCurrent = index === illustrationIndex;
            return (
              <Image
                key={illustration.key}
                src={illustration.src}
                // 表示していない絵は読み上げ対象から外す
                alt={isCurrent ? illustration.alt : ""}
                width={112}
                height={112}
                sizes="112px"
                className={`absolute inset-0 size-full object-contain ${
                  isCurrent ? "" : "invisible"
                }`}
              />
            );
          })}
        </span>

        <span className="min-w-0 flex-1">
          {/*
            「めんれん先輩からのアドバイス」は本文列が約220pxを下回ると2行に折り返る。
            折り返しが起きる幅（360px未満）だけ2行ぶんの高さを確保し、種類によって
            カードの高さが動かないようにする。それ以上の幅では1行に収まるので予約しない。
          */}
          <span
            className={`line-clamp-2 block text-base font-bold max-[360px]:min-h-12 md:text-lg ${KIND_LABEL_CLASS[tip.kind]}`}
          >
            {WAITING_TIP_KIND_LABELS[tip.kind]}
          </span>
          {/*
            3行ぶんの高さを確保し、同時に3行で打ち切る。こうすると文言の長さに
            かかわらず高さが変わらず、切り替えのたびにレイアウトが動かない。
          */}
          <span className="mt-1 line-clamp-3 block min-h-[4.25rem] text-sm leading-relaxed text-on-surface">
            {tip.body}
          </span>
          {/*
            人物名の行は、アドバイス（人物名なし）でも高さを確保する。
            種類によって高さが変わるとカードの高さも動いてしまう。
          */}
          <span className="mt-0.5 block min-h-4 text-xs text-subtext">
            {tip.source ? `— ${tip.source}` : ""}
          </span>
        </span>
      </span>
    </button>
  );
}
